import { useMemo } from "react";
import { Form, Formik } from "formik";
import { lazy } from "yup";
import {
  balanceDetailsIV,
  balanceIV,
  balanceSchema,
  getBalanceSchema,
} from "../../models/balanceTenant";
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import { useToast } from "../UI/Toast/useToast";
import { handleAPIError, isChargeOrTravauxType } from "../../utils/index.jsx";
import BalanceFields from "./BalanceFields";
import styles from "./BalanceForm.module.css";
import Button from "../UI/Button";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import {
  getBalanceParentId,
  getParentIdPropertyFromDomain,
  initializeRefacturationValues,
  isChildRecuperable,
  shouldSplitBalance,
  toBalanceDetailsPayload,
} from "../../utils/balances";
import { useBalanceTypes } from "../../hooks/use-balances";
import usePropertyFolders from "../../hooks/use-property-folders";

export default function BalanceForm({
  balance,
  open,
  onOpenChange,
  onSubmit,
  onDelete,
  domaineBalance,
  parentId,
}) {
  const isFromTransac = balance && balance.id_transaction;
  const isGrouped = balance?.balances?.length > 0;
  const isFromSplit = balance && balance.correlation_id && !isGrouped;
  const isEdit = !!balance;
  const toast = useToast();
  const { balanceTypes, balanceTypesByCategory } = useBalanceTypes();
  const { propertyFoldersById } = usePropertyFolders({ withById: true });

  // Une ventilation existante se modifie en place : la ligne qui la regroupe n'a pas d'id
  // propre, aucune route ne la déplace. Elle garde la disposition d'origine.
  // Mémorisé pour ne pas recalculer le sous-arbre d'affectation à chaque frappe.
  const context = useMemo(
    () => (isGrouped ? undefined : { domaine: domaineBalance, parentId }),
    [isGrouped, domaineBalance, parentId],
  );

  // Le champ n'est exigé que là où le formulaire le propose : sans contexte, aucun
  // champ ne l'écrit.
  const validationSchema = lazy(() => {
    const schema = getBalanceSchema(balanceTypes);
    return context ? schema : schema.omit(["affectation"]);
  });

  const initialValues = {
    ...toInitialValuesFromSchema(balanceSchema, balance, balanceIV),
    is_remboursement: false,
  };

  if (
    isEdit &&
    balance &&
    balance.montant > 0 &&
    isChargeOrTravauxType(balance.type, balanceTypesByCategory)
  ) {
    initialValues.is_remboursement = true;
  }
  initialValues.files = initialValues.file ? [initialValues.file] : [];
  // Le détail arrive `null` du back : chaque champ repart d'une chaîne vide pour rester
  // contrôlé.
  initialValues.details = {
    ...balanceDetailsIV,
    ...Object.fromEntries(
      Object.entries(balance?.details ?? {}).filter(
        ([, value]) => value !== null && value !== undefined,
      ),
    ),
  };
  if (initialValues.domaine === "") initialValues.domaine = domaineBalance;
  if (!getBalanceParentId(initialValues))
    initialValues[getParentIdPropertyFromDomain(domaineBalance)] = parentId;
  if (initialValues.balances.length > 0) {
    initialValues.periode_debut = initialValues.balances[0].periode_debut || "";
    initialValues.periode_fin = initialValues.balances[0].periode_fin || "";
  }

  initialValues.refacturation_values = [];
  if (isEdit && isChildRecuperable(initialValues, balanceTypes)) {
    initialValues.refacturation_values = initializeRefacturationValues(balance);
  }

  async function submitHandler(values, { resetForm }) {
    if (isFromSplit) return onOpenChange(false);

    const sanitizedValues = { ...sanitizeValues(values) };
    sanitizedValues.file = sanitizedValues.files[0] || null;
    delete sanitizedValues.files;
    sanitizedValues.details = toBalanceDetailsPayload(values.details);

    sanitizedValues.refacturations = {};
    sanitizedValues.refacturation_values.forEach((r) => {
      sanitizedValues.refacturations[r.tenant_id] = r.date;
    });
    delete sanitizedValues.refacturation_values;

    try {
      await onSubmit(sanitizedValues);
      resetForm();
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
    >
      {({ resetForm, values }) => (
        <ModalDialog
          size="sm"
          open={open}
          header={balance ? "Modifier une opération" : "Ajouter une opération"}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
            }
            onOpenChange(nextOpen);
          }}
        >
          {isFromTransac && !isFromSplit && (
            <p className={"secondaryText marginB10 text-center"}>
              Vous ne pouvez pas éditer le montant ni la description, car cette
              entrée a été exportée depuis vos relevés de comptes.
            </p>
          )}
          {isFromSplit && (
            <p className={"secondaryText marginB10 text-center"}>
              Vous ne pouvez pas éditer ni supprimer cette entrée, car elle est
              répartie depuis le dossier parent.
            </p>
          )}
          <Form>
            <ModalDialogBody className={styles.fields}>
              <BalanceFields
                isFromTransac={isFromTransac}
                isFromSplit={isFromSplit}
                domaineBalance={domaineBalance}
                balanceId={
                  balance
                    ? isGrouped
                      ? balance.balances[0].id
                      : balance.id
                    : null
                }
                parentId={parentId}
                context={context}
                withJustificatif
                isSplit={
                  shouldSplitBalance(
                    values.domaine,
                    getBalanceParentId(values),
                    propertyFoldersById,
                  ) || isGrouped
                }
                childBalances={isGrouped && balance.balances}
                isEdit={isEdit}
                isRecuperable={isChildRecuperable(values, balanceTypes)}
              />
            </ModalDialogBody>

            <ModalDialogFooter>
              {balance && (
                <Button
                  requiresWrite
                  type={"button"}
                  variant="secondary"
                  disabled={isFromSplit}
                  onClick={async () => {
                    if (
                      window.confirm(
                        "Voulez-vous vraiment supprimer cette entrée ?",
                      )
                    ) {
                      try {
                        await onDelete(
                          isGrouped ? balance.correlation_id : balance.id,
                          isGrouped,
                        );
                      } catch (error) {
                        console.error(error);
                      }
                    }
                  }}
                >
                  Supprimer
                </Button>
              )}
              <FormikSubmitButton requiresWrite>
                {isFromSplit ? "Fermer" : "Valider"}
              </FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
